/**
 * Vincent B972 + B973 (Eric 2026-02-26 SPEC STRICK PROD) — Barre d'actions
 * du sous-onglet Fusionnée, extraite de TranscriptPanel.js pour aligner
 * l'architecture avec WhisperToolbar / FirefliesSubTab (single-block).
 *
 * Ordre EXACT demandé par l'utilisateur (B973, revu par les tâches 1306 ·
 * 1307 — les boutons ne répètent plus le mot porté par le libellé de bloc) :
 *   0. « Fusion : »                    (ToolbarGroupLabel)
 *   1. Réglages                        (GROUP_SETTINGS — passé en prop)
 *   2. Refaire                         (GROUP_RERUN    — passé en prop)
 *   3. Diagnostic                      (GROUP_UTIL)    disabled si !has_merged
 *   4. Rapport                         (GROUP_UTIL)    disabled si !has_merged
 *   5. Purger doublons                 (GROUP_UTIL)    disabled si !has_merged
 *   6. [Séparateur]
 *   7. « Orthographe : »               (ToolbarGroupLabel)
 *   8. Détecter                        (GROUP_UTIL)    disabled si !has_merged
 *   9. Corrections en attente          (GROUP_RERUN)   disabled si pending===0
 *  10. Diagnostic                      (GROUP_UTIL)    disabled si !has_merged
 *  11. [Séparateur]
 *  12. Jargon                          (GROUP_UTIL)    disabled si !has_merged
 *  13. URLs                            (GROUP_UTIL)    disabled si !has_merged
 *  14. Historique                      (GROUP_UTIL)    disabled si !has_merged
 *
 * Philosophie B971 : tous les boutons sont TOUJOURS visibles ; ils sont
 * simplement `disabled` quand les conditions ne sont pas remplies, avec
 * un tooltip explicatif au survol.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import FusionDiagnosticModal from "./FusionDiagnosticModal";
import SpellingDiagnosticModal from "./SpellingDiagnosticModal";
import ManualEditsJournalModal from "./ManualEditsJournalModal";
import RaccourcisClavierModal, { CLE_RACCOURCIS_VUS } from "./RaccourcisClavierModal";
import { GROUP_UTIL, GROUP_RERUN, ToolbarSeparator, ToolbarGroupLabel } from "../../lib/transcriptToolbarStyles";
import { useCollapsedToolbarGroups } from "../../lib/useCollapsedToolbarGroups";

export const MergedToolbar = ({
  show,
  canEditKnowledge,
  lang,
  api,
  videoId,
  allTranscripts,
  // Pre-built shared buttons (Réglages fusion + Refaire la fusion) créés
  // par TranscriptPanel car ils partagent state (crossValidating, etc.).
  reglagesFusionBtn,
  refaireFusionBtn,
  // Handlers
  crossValidating,
  setCrossValidating,
  loadAllTranscripts,
  loadVideoData,
  openReportModal,
  setShowPendingCorrections,
  setUrlValidating,
  setUrlProposals,
  urlValidating,
  setShowHistory,
  loadRevisions,
}) => {
  // Tâches 1273 et 1274 (Eric 2026-06-29) — Les deux diagnostics s'ouvraient
  // dans une boîte système. Ils ont maintenant leur propre fenêtre, comme
  // « Réglages fusion ».
  const [fusionDiagOpen, setFusionDiagOpen] = useState(false);
  const [spellingDiagOpen, setSpellingDiagOpen] = useState(false);
  // Tâche 1333 — Dernière correction non traitée atteinte, pour que les deux
  // flèches avancent au lieu de ramener toujours à la même ligne.
  const [lastPendingIdx, setLastPendingIdx] = useState(null);
  // Tâche 1339 — Journal des retouches à la main.
  const [journalOpen, setJournalOpen] = useState(false);
  // Tâche 1342 (Eric 2026-06-30) — Relecture guidée : après chaque
  // acceptation, on enchaîne tout seul sur la correction non traitée
  // suivante. Le choix est mémorisé, car on ne veut pas le reprendre à
  // chaque ouverture, et désactivable, car sauter d'une ligne à l'autre
  // dérange quand on traite une seule correction au passage.
  const [relectureGuidee, setRelectureGuidee] = useState(() => {
    try {
      return window.localStorage.getItem("knoria_relecture_guidee") !== "0";
    } catch {
      return true;
    }
  });
  // Tâche 1343 — Panneau des raccourcis, ouvert d'office au premier passage.
  const [raccourcisOpen, setRaccourcisOpen] = useState(false);
  // Tâche 1312 — Chaque bloc de boutons se replie, et le choix est mémorisé.
  const { isCollapsed, toggleGroup } = useCollapsedToolbarGroups();

  // Tâche 1338 (Eric 2026-06-30) — Relire une longue transcription à la souris
  // impose un aller-retour constant entre la ligne et la barre d'outils.
  // Alt + flèche saute d'une correction non traitée à l'autre. La touche Alt
  // est obligatoire : une simple flèche déplacerait le curseur pendant qu'on
  // tape une correction. Le raccourci est donc ignoré dans un champ de texte.
  const goToPendingRef = useRef(() => {});
  // Tâche 1346 — Lu au moment du saut, pas au moment du branchement : la
  // liste des corrections a changé entre les deux.
  const pendingRestantRef = useRef(() => 0);
  useEffect(() => {
    if (!show) return undefined;
    const surTouche = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      const cible = e.target;
      const balise = (cible?.tagName || "").toLowerCase();
      if (balise === "input" || balise === "textarea" || cible?.isContentEditable) return;
      e.preventDefault();
      goToPendingRef.current(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [show]);

  // Tâche 1342 — Le sous-onglet Fusionnée annonce chaque acceptation ; c'est
  // ici qu'on décide d'enchaîner, parce que c'est ici que vit la liste des
  // corrections restantes.
  useEffect(() => {
    if (!show) return undefined;
    const surAcceptation = () => {
      if (!relectureGuidee) return;
      // Le rechargement des segments arrive juste après l'acceptation : on
      // laisse la nouvelle liste se poser avant de choisir la cible.
      setTimeout(() => {
        // Tâche 1346 — Fin de parcours : on le DIT, au lieu de laisser la
        // surbrillance tourner en rond sur la dernière ligne.
        if (pendingRestantRef.current() === 0) {
          toast.success(lang === "fr"
            ? "Relecture terminée : plus aucune correction en attente."
            : "Review complete: no pending correction left.");
          return;
        }
        goToPendingRef.current(1);
      }, 400);
    };
    window.addEventListener("knoria:merged-correction-accepted", surAcceptation);
    return () => window.removeEventListener("knoria:merged-correction-accepted", surAcceptation);
  }, [show, relectureGuidee, lang]);

  // Tâche 1343 — Première arrivée sur la fusion : on montre les raccourcis
  // une fois. Une infobulle ne se lit qu'au survol du bouton qu'on cherchait
  // justement à éviter.
  useEffect(() => {
    if (!show) return;
    try {
      if (window.localStorage.getItem(CLE_RACCOURCIS_VUS) === "1") return;
      window.localStorage.setItem(CLE_RACCOURCIS_VUS, "1");
      setRaccourcisOpen(true);
    } catch { /* stockage indisponible : on n'insiste pas */ }
  }, [show]);

  if (!show) return null;

  const hasMerged = !!allTranscripts?.summary?.has_merged;
  const disabledIfNoMergedTitle = lang === "fr"
    ? "Aucune transcription fusionnée disponible. Génère la fusion (Whisper + Fireflies) pour activer ce bouton."
    : "No merged transcript available. Generate the fusion (Whisper + Fireflies) first.";

  // Nombre de corrections orthographiques en attente (pour le bouton
  // « Corrections en attente »). Le bouton reste toujours visible mais
  // devient disabled quand rien à traiter.
  const pendingCount = (allTranscripts?.merged?.segments || []).reduce(
    (n, s) => n + ((s.corrections || []).filter((c) => !c.accepted && !c.rejected).length),
    0,
  );

  // Tâche 1333 (Eric 2026-06-30) — Navigation d'une correction non traitée à
  // la suivante. On repère les phrases qui en portent encore, puis on demande
  // au sous-onglet Fusionnée de surligner et d'amener celle-ci à l'écran
  // (même canal que le bouton « Corriger » de l'aperçu, B766).
  const pendingSegmentIndexes = (allTranscripts?.merged?.segments || []).reduce(
    (acc, s, i) => {
      if ((s.corrections || []).some((c) => !c.accepted && !c.rejected)) acc.push(i);
      return acc;
    },
    [],
  );
  const goToPending = (direction) => {
    if (pendingSegmentIndexes.length === 0) return;
    const courant = lastPendingIdx;
    let cible;
    if (direction > 0) {
      cible = pendingSegmentIndexes.find((i) => courant === null || i > courant);
      if (cible === undefined) cible = pendingSegmentIndexes[0];
    } else {
      const avant = pendingSegmentIndexes.filter((i) => courant === null || i < courant);
      cible = avant.length ? avant[avant.length - 1] : pendingSegmentIndexes[pendingSegmentIndexes.length - 1];
    }
    setLastPendingIdx(cible);
    window.dispatchEvent(new CustomEvent("knoria:merged-focus-segment", {
      // `persist` : la surbrillance sert de curseur de relecture et doit
      // rester jusqu'au saut suivant (tâche 1338, Alt + Entrée).
      detail: { videoId, segmentIndex: cible, persist: true },
    }));
  };
  goToPendingRef.current = goToPending;
  pendingRestantRef.current = () => pendingSegmentIndexes.length;

  // Tâche 1339 — Phrases réécrites à la main (données déjà chargées).
  const retouchesCount = (allTranscripts?.merged?.segments || [])
    .filter((s) => s.manual_edit).length;

  const handleRunSpellingCorrection = async () => {
    if (crossValidating) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Lancer une correction linguistique automatique (orthographe, accents, ponctuation) ? Les propositions seront appliquées directement — utilisez le ✗ pour rejeter une par une."
        : "Run automatic linguistic correction? Changes are applied live — use ✗ to reject individual ones.",
      { tone: "warning" },
    );
    if (!ok) return;
    setCrossValidating(true);
    try {
      const { data: proposed } = await axios.post(
        `${api}/api/videos/${videoId}/propose-linguistic-corrections`,
        {},
        { withCredentials: true },
      );
      const totalBatches = proposed.total_batches ?? 0;
      const failed = proposed.failed_batches ?? 0;
      if ((proposed.corrections || []).length === 0) {
        if (failed > 0) {
          const firstErr = (proposed.errors && proposed.errors[0]) || "";
          toast.error(
            lang === "fr"
              ? `Correction orthographe partielle : ${failed}/${totalBatches} lot(s) GPT-5.2 en échec. ${firstErr}`
              : `Spelling correction partial: ${failed}/${totalBatches} GPT-5.2 batch(es) failed. ${firstErr}`,
          );
        } else {
          toast.success(
            lang === "fr" ? "Aucune correction orthographique nécessaire." : "No spelling correction needed.",
          );
        }
        setCrossValidating(false);
        return;
      }
      await axios.post(
        `${api}/api/videos/${videoId}/apply-linguistic-corrections`,
        { corrections: proposed.corrections },
        { withCredentials: true },
      );
      if (failed > 0) {
        toast.warning(
          lang === "fr"
            ? `${proposed.corrections.length} corrections appliquées mais ${failed}/${totalBatches} lot(s) GPT-5.2 ont échoué — relance pour compléter.`
            : `${proposed.corrections.length} corrections applied but ${failed}/${totalBatches} GPT-5.2 batch(es) failed — rerun to complete.`,
        );
      } else {
        toast.success(
          lang === "fr"
            ? `${proposed.corrections.length} corrections appliquées`
            : `${proposed.corrections.length} corrections applied`,
        );
      }
      if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
      if (typeof loadVideoData === "function") await loadVideoData();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`,
      );
    }
    setCrossValidating(false);
  };

  const handleRunJargonPass = async () => {
    // Tâche 1259 — Passe jargon : casse et accents des termes du dictionnaire.
    // Aucun appel IA ; elle tourne déjà d'office après une copie mono-source,
    // ce bouton sert à la rejouer après une mise à jour du dictionnaire.
    if (crossValidating) return;
    const ok = await confirmDialog(
      lang === "fr"
        ? "Rétablir la casse et les accents des termes du dictionnaire (« celiapp » → « CELIAPP », « manoeuvre » → « manœuvre ») ? Aucun mot n'est remplacé et aucun appel à l'IA n'est fait. Chaque correction reste annulable d'un ✗."
        : "Restore casing and accents of dictionary terms? No word is replaced, no AI call. Each correction stays reversible.",
      { tone: "warning" },
    );
    if (!ok) return;
    setCrossValidating(true);
    try {
      const { data: proposed } = await axios.post(
        `${api}/api/videos/${videoId}/propose-jargon-corrections`,
        {},
        { withCredentials: true },
      );
      const found = (proposed.corrections || []).length;
      if (found === 0) {
        toast.success(
          lang === "fr"
            ? `Aucun terme à rétablir (${proposed.terms_scanned} terme(s) du dictionnaire vérifié(s)).`
            : `Nothing to fix (${proposed.terms_scanned} dictionary term(s) checked).`,
        );
        setCrossValidating(false);
        return;
      }
      await axios.post(
        `${api}/api/videos/${videoId}/apply-linguistic-corrections`,
        { corrections: proposed.corrections },
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `${found} terme(s) du dictionnaire rétabli(s) — sans aucun appel IA.`
          : `${found} dictionary term(s) restored — with zero AI calls.`,
      );
      if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
      if (typeof loadVideoData === "function") await loadVideoData();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") + (err.response?.data?.detail || err.message),
      );
    }
    setCrossValidating(false);
  };

  const handleSpellingDiagnostic = () => setSpellingDiagOpen(true);

  const handleFusionDiagnostic = () => setFusionDiagOpen(true);

  const handleDedupeCorrections = async () => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Purger les corrections linguistiques dupliquées de cette vidéo ? (opération sûre : garde 1 exemplaire par couple original/correction ; préserve les corrections déjà ✓ acceptées)"
        : "Purge duplicated linguistic corrections from this video? (safe: keeps 1 per original/correction pair, preserves ✓ accepted ones)",
      { tone: "warning", confirmLabel: lang === "fr" ? "Purger" : "Purge" },
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/dedupe-corrections`,
        {},
        { withCredentials: true },
      );
      const removed = data?.duplicates_removed ?? 0;
      const scanned = data?.segments_scanned ?? 0;
      if (removed === 0) {
        toast.success(lang === "fr" ? `Aucun doublon trouvé (${scanned} segment(s) analysé(s)).` : `No duplicates found (${scanned} segments scanned).`);
      } else {
        toast.success(lang === "fr" ? `${removed} doublon(s) purgé(s) sur ${scanned} segment(s).` : `${removed} duplicate(s) purged on ${scanned} segments.`);
      }
      loadAllTranscripts?.();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
      toast.error(lang === "fr" ? `Purge échouée : ${d}` : `Purge failed: ${d}`);
    }
  };

  const handleValidateUrls = async () => {
    if (urlValidating) return;
    setUrlValidating(true);
    setUrlProposals(null);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/validate-urls`,
        {},
        { withCredentials: true },
      );
      setUrlProposals(data);
    } catch (err) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setUrlValidating(false);
    }
  };

  // Non-éditeur : rien à afficher (parité avec l'ancien comportement où
  // les boutons étaient gated par canEditKnowledge en Line 2).
  if (!canEditKnowledge) return null;

  return (
    <>
      {/* Tâche 1307 (Eric 2026-06-30) — Le mot « Fusion : » porte ce que les
          boutons ne répètent plus (« Réglages », « Refaire », « Rapport »). */}
      <ToolbarGroupLabel
        testid="toolbar-group-label-fusion"
        collapsed={isCollapsed("fusion")}
        onToggle={() => toggleGroup("fusion")}
        count={5}
      >
        {lang === "fr" ? "Fusion :" : "Fusion:"}
      </ToolbarGroupLabel>
      {!isCollapsed("fusion") && (
        <>
      {/* 1. Réglages (pre-built par TranscriptPanel) */}
      {reglagesFusionBtn}
      {/* 2. Refaire (pre-built par TranscriptPanel) */}
      {refaireFusionBtn}
      {/* 3. Diagnostic (fusion) */}
      <button
        type="button"
        onClick={handleFusionDiagnostic}
        disabled={!hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMerged ? disabledIfNoMergedTitle : (lang === "fr" ? "Stats internes de la transcription fusionnée (segments, corrections, sources)" : "Internal stats of the merged transcript")}
        data-testid="open-fusion-diagnostic-btn"
        data-button-group="util"
        data-visible-in-subtab="merged"
      >
        🔎 {lang === "fr" ? "Diagnostic" : "Diagnostic"}
      </button>
      {/* 4. Rapport */}
      <button
        type="button"
        onClick={() => openReportModal("fusion")}
        disabled={!hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMerged ? disabledIfNoMergedTitle : (lang === "fr" ? "Voir le rapport d'analyse de fusion (tableau comparatif + corrections de jargon)" : "View the fusion analysis report")}
        data-testid="open-fusion-report-btn"
        data-button-group="util"
        data-visible-in-subtab="merged"
      >
        📊 {lang === "fr" ? "Rapport" : "Report"}
      </button>
      {/* 5. Purger doublons */}
      <button
        type="button"
        onClick={handleDedupeCorrections}
        disabled={!hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMerged ? disabledIfNoMergedTitle : (lang === "fr" ? "Purger les corrections linguistiques dupliquées" : "Purge duplicated linguistic corrections")}
        data-testid="dedupe-corrections-btn"
        data-button-group="util"
        data-visible-in-subtab="merged"
      >
        🧹 {lang === "fr" ? "Purger doublons" : "Purge duplicates"}
      </button>
        </>
      )}
      {/* 6. Séparateur — entre bloc « Fusion » et bloc « Orthographe » */}
      <ToolbarSeparator />
      {/* Tâche 1306 — Le mot « Orthographe : » devant ses deux boutons
          devenus « Détecter » et « Diagnostic ». */}
      <ToolbarGroupLabel
        testid="toolbar-group-label-orthographe"
        collapsed={isCollapsed("orthographe")}
        onToggle={() => toggleGroup("orthographe")}
        count={3}
      >
        {lang === "fr" ? "Orthographe :" : "Spelling:"}
      </ToolbarGroupLabel>
      {!isCollapsed("orthographe") && (
        <>
      {/* 7. Détecter (ex « Orthographe ») */}
      <button
        type="button"
        onClick={handleRunSpellingCorrection}
        disabled={crossValidating || !hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-40 disabled:cursor-not-allowed`}
        title={!hasMerged
          ? disabledIfNoMergedTitle
          : (lang === "fr"
              ? "Corriger automatiquement orthographe, accents et ponctuation (sans modifier le sens)."
              : "Auto-correct orthography, accents and punctuation.")}
        data-testid="run-spelling-correction-btn"
        data-button-group="action"
        data-visible-in-subtab="merged"
      >
        {crossValidating ? "⏳" : "✨"} {lang === "fr" ? "Détecter" : "Detect"}
      </button>
      {/* 8. Corrections en attente — toujours visible ; disabled si aucune. */}
      <button
        type="button"
        onClick={() => setShowPendingCorrections(true)}
        disabled={!hasMerged || pendingCount === 0}
        className={`${GROUP_RERUN} text-white disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMerged
          ? disabledIfNoMergedTitle
          : (pendingCount === 0
              ? (lang === "fr"
                  ? "Aucune correction orthographique en attente."
                  : "No pending spelling corrections.")
              : (lang === "fr"
                  ? "Traiter plusieurs corrections en attente en un clic"
                  : "Batch-process several pending corrections"))}
        data-testid="open-pending-corrections-btn"
        data-visible-in-subtab="merged"
      >
        🗂 {lang === "fr" ? `Corrections en attente (${pendingCount})` : `Pending corrections (${pendingCount})`}
      </button>
      {/* Tâche 1333 (Eric 2026-06-30) — Deux flèches pour passer d'une
          correction NON TRAITÉE à la suivante. Sans elles, terminer une
          relecture obligeait à faire défiler toute la transcription à la
          recherche des lignes restantes. Elles n'apparaissent que s'il reste
          du travail, et disparaissent dès que tout est accepté ou refusé. */}
      {pendingCount > 0 && (
        <>
          <button
            type="button"
            onClick={() => goToPending(-1)}
            className={`${GROUP_UTIL} px-2`}
            data-testid="merged-pending-prev-btn"
            aria-label={lang === "fr" ? "Correction non traitée précédente" : "Previous unhandled correction"}
            title={lang === "fr"
              ? "Aller à la correction non traitée précédente (Alt + flèche gauche)"
              : "Go to the previous unhandled correction (Alt + Left arrow)"}
          >
            ◀
          </button>
          <button
            type="button"
            onClick={() => goToPending(1)}
            className={`${GROUP_UTIL} px-2`}
            data-testid="merged-pending-next-btn"
            aria-label={lang === "fr" ? "Correction non traitée suivante" : "Next unhandled correction"}
            title={lang === "fr"
              ? "Aller à la correction non traitée suivante (Alt + flèche droite · Alt + Entrée pour accepter · Alt + Retour arrière pour refuser)"
              : "Go to the next unhandled correction (Alt + Right arrow · Alt + Enter to accept · Alt + Backspace to reject)"}
          >
            ▶
          </button>
          {/* Tâche 1346 (Eric 2026-06-30) — Compteur de ce qui RESTE, entre
              les deux flèches. Le bouton voisin donne bien le total, mais il
              est loin du regard pendant qu'on enchaîne les corrections. */}
          <span
            className="text-[11px] font-semibold text-amber-800 px-1"
            data-testid="merged-pending-remaining"
            data-remaining={String(pendingCount)}
            title={lang === "fr"
              ? `${pendingCount} correction(s) encore à accepter ou à refuser`
              : `${pendingCount} correction(s) left to accept or reject`}
          >
            {lang === "fr" ? `${pendingCount} restante${pendingCount > 1 ? "s" : ""}` : `${pendingCount} left`}
          </span>
          {/* Tâche 1342 — Enchaînement automatique, mémorisé. */}
          <label
            className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600 px-1.5 cursor-pointer select-none"
            title={lang === "fr"
              ? "Sauter tout seul à la correction non traitée suivante dès qu'une correction est acceptée."
              : "Jump to the next unhandled correction on its own as soon as one is accepted."}
            data-testid="merged-guided-review-label"
          >
            <input
              type="checkbox"
              checked={relectureGuidee}
              onChange={(e) => {
                setRelectureGuidee(e.target.checked);
                try {
                  window.localStorage.setItem("knoria_relecture_guidee", e.target.checked ? "1" : "0");
                } catch { /* stockage indisponible */ }
              }}
              className="w-3.5 h-3.5 accent-[#002FA7]"
              data-testid="merged-guided-review-checkbox"
            />
            {lang === "fr" ? "Relecture guidée" : "Guided review"}
          </label>
        </>
      )}
      {/* 9. Diagnostic (orthographe) */}
      <button
        type="button"
        onClick={handleSpellingDiagnostic}
        disabled={!hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={!hasMerged ? disabledIfNoMergedTitle : (lang === "fr" ? "Stats des corrections orthographiques (acceptées / rejetées / dupliquées)" : "Spelling corrections stats")}
        data-testid="open-spelling-diagnostic-btn"
        data-button-group="util"
        data-visible-in-subtab="merged"
      >
        🔎 {lang === "fr" ? "Diagnostic" : "Diagnostic"}
      </button>
        </>
      )}
      {/* 10. Séparateur — entre bloc « Orthographe » et bloc final. */}
      <ToolbarSeparator />
      {/* 11. Jargon (Tâche 1259) — gratuit, déterministe, dictionnaire seul.
          Tâche 1306 : déplacé après le séparateur, juste avant « URLs ». */}
      <button
        type="button"
        onClick={handleRunJargonPass}
        disabled={crossValidating || !hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-40 disabled:cursor-not-allowed`}
        title={!hasMerged
          ? disabledIfNoMergedTitle
          : (lang === "fr"
              ? "Rétablir la casse et les accents des termes du dictionnaire (aucun appel IA, aucun mot remplacé). Déjà fait d'office après une copie fidèle."
              : "Restore casing and accents of dictionary terms (no AI call, no word replaced).")}
        data-testid="run-jargon-pass-btn"
        data-button-group="action"
        data-visible-in-subtab="merged"
      >
        {crossValidating ? "⏳" : "📘"} {lang === "fr" ? "Jargon" : "Jargon"}
      </button>
      {/* 12. URLs */}
      <button
        type="button"
        onClick={handleValidateUrls}
        disabled={urlValidating || !hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="validate-urls-merged-btn"
        data-button-group="util"
        title={!hasMerged
          ? disabledIfNoMergedTitle
          : (lang === "fr" ? "Detecter les URLs prononcees et vérifier qu'elles existent" : "Detect spoken URLs and verify they exist")}
      >
        🔗 {urlValidating ? "..." : "URLs"}
      </button>
      {/* 13. Historique */}
      <button
        type="button"
        onClick={() => { setShowHistory(true); loadRevisions(); }}
        disabled={!hasMerged}
        className={`${GROUP_UTIL} disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="merged-transcript-history-btn"
        data-button-group="util"
        title={!hasMerged ? disabledIfNoMergedTitle : (lang === "fr" ? "Historique des modifications" : "Modification history")}
      >
        ⏱ {lang === "fr" ? "Historique" : "History"}
      </button>
      {/* Tâche 1343 — Rappel des raccourcis, toujours à portée, même quand
          il n'y a plus rien à corriger. */}
      <button
        type="button"
        onClick={() => setRaccourcisOpen(true)}
        className={`${GROUP_UTIL} px-2`}
        data-testid="merged-shortcuts-help-btn"
        data-button-group="util"
        data-visible-in-subtab="merged"
        aria-label={lang === "fr" ? "Voir les raccourcis clavier" : "See keyboard shortcuts"}
        title={lang === "fr"
          ? "Raccourcis clavier pour relire une transcription sans la souris"
          : "Keyboard shortcuts to review a transcript without the mouse"}
      >
        ⌨
      </button>
      {/* 14. Tâche 1339 — Retouches à la main. Le bouton ne s'affiche que
          s'il y a quelque chose à montrer : un bouton « (0) » toujours
          présent n'apprend rien et encombre la barre. */}
      {retouchesCount > 0 && (
        <button
          type="button"
          onClick={() => setJournalOpen(true)}
          className={GROUP_UTIL}
          data-testid="merged-manual-edits-journal-btn"
          data-button-group="util"
          data-visible-in-subtab="merged"
          title={lang === "fr"
            ? "Qui a réécrit quelle phrase à la main, avec la version d'avant et un retour en un clic."
            : "Who rewrote which sentence by hand, with the previous version and a one-click revert."}
        >
          ✎ {lang === "fr"
            ? `Retouches à la main (${retouchesCount})`
            : `Hand edits (${retouchesCount})`}
        </button>
      )}
      <ManualEditsJournalModal
        open={journalOpen}
        onClose={() => setJournalOpen(false)}
        api={api}
        videoId={videoId}
        segments={allTranscripts?.merged?.segments || []}
        canEdit={!!canEditKnowledge}
        onRestored={(segId) => loadAllTranscripts && loadAllTranscripts(segId)}
        lang={lang}
      />
      <RaccourcisClavierModal
        open={raccourcisOpen}
        onClose={() => setRaccourcisOpen(false)}
        lang={lang}
      />
      {/* Tâches 1273 · 1274 — Les fenêtres des deux diagnostics. */}
      <FusionDiagnosticModal
        open={fusionDiagOpen}
        onClose={() => setFusionDiagOpen(false)}
        merged={allTranscripts?.merged}
        lang={lang}
      />
      <SpellingDiagnosticModal
        open={spellingDiagOpen}
        onClose={() => setSpellingDiagOpen(false)}
        merged={allTranscripts?.merged}
        lang={lang}
      />
    </>
  );
};

export default MergedToolbar;
